import { NavLink, useLocation, useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useTicketStatsQuery } from "@/hooks/useTickets";
import { useInboxStatsQuery } from "@/hooks/useInbox";
import { usePhoneStatsQuery } from "@/hooks/usePhone";
import { useCareRequestStatsQuery } from "@/hooks/useCareRequests";
import { useReferralNotificationCountsQuery } from "@/hooks/useReferrals";
import { usePendingApprovalsCountQuery } from "@/hooks/usePendingApprovals";
import { usePermission } from "@/hooks/usePermission";
import { useThemeMode } from "@/context/ThemeContext";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { motion, AnimatePresence } from "framer-motion";
import { prefetchRoute } from "@/routes/prefetch";
import {
    LayoutDashboard,
    Calendar,
    MessageSquare,
    ClipboardList,
    Phone,
    Video,
    Users,
    Receipt,
    Settings,
    ChevronLeft,
    LogOut,
    Activity,
    Sun,
    Moon,
    Monitor,
    DoorOpen,
    UserCheck,
    CheckSquare,
    BarChart3,
    Bell,
    RotateCcw,
    CalendarRange,
    ArrowRightLeft,
    Building2,
    ShieldCheck,
    Globe,
    ClipboardCheck,
    AlertTriangle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEscalationStatsQuery } from "@/hooks/useEscalations";
import { useFeatureGateContext } from "@/contexts/FeatureGateContext";
import type { PracticeFeatureKey } from "@/types/practice";

/** Sidebar-Navigations-Item mit optionalem Feature-Gate (Rule 176) */
interface NavItem {
    to: string;
    label: string;
    icon: LucideIcon;
    shortcut?: string;
    badge?:
        | "unread"
        | "tickets"
        | "missed"
        | "careRequests"
        | "referrals"
        | "pendingApprovals"
        | "escalations";
    group: "main" | "communication" | "data";
    parentTo?: string;
    /** Plan-gesteuertes Feature — Item wird unsichtbar wenn deaktiviert */
    featureKey?: PracticeFeatureKey;
}

const NAV_ITEMS: NavItem[] = [
    {
        to: "/app/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        shortcut: "1",
        group: "main",
    },
    { to: "/app/calendar", label: "Kalender", icon: Calendar, shortcut: "2", group: "main", featureKey: "onlineBooking" },
    { to: "/app/waiting-room", label: "Wartezimmer", icon: DoorOpen, shortcut: "3", group: "main", featureKey: "waitlistManagement" },
    { to: "/app/reception", label: "Rezeption", icon: UserCheck, shortcut: "4", group: "main" },
    {
        to: "/app/inbox",
        label: "Postfach",
        icon: MessageSquare,
        shortcut: "5",
        badge: "unread",
        group: "communication",
        featureKey: "inAppMessaging",
    },
    {
        to: "/app/tickets",
        label: "Tickets",
        icon: ClipboardList,
        shortcut: "6",
        badge: "tickets",
        group: "communication",
    },
    {
        to: "/app/ai-phone",
        label: "KI-Telefon",
        icon: Phone,
        shortcut: "7",
        badge: "missed",
        group: "communication",
        featureKey: "aiPhone",
    },
    {
        to: "/app/video-consultation",
        label: "Videosprechstunde",
        icon: Video,
        group: "communication",
        featureKey: "videoConsultation",
    },
    {
        to: "/app/triage",
        label: "KI-Triage",
        icon: Activity,
        shortcut: "8",
        group: "communication",
        featureKey: "aiTriage",
    },
    {
        to: "/app/care-requests",
        label: "Pflegeanfragen",
        icon: Building2,
        badge: "careRequests",
        group: "data",
        parentTo: "/app/netzwerk",
    },
    {
        // P-W34 Phase 3 — Cross-Profession Eskalations-Inbox (Care → Praxis)
        to: "/app/eskalationen",
        label: "Eskalationen",
        icon: AlertTriangle,
        badge: "escalations",
        group: "data",
        parentTo: "/app/netzwerk",
    },
    { to: "/app/netzwerk", label: "Netzwerk", icon: Globe, group: "data" },
    { to: "/app/patients", label: "Patienten", icon: Users, group: "data" },
    { to: "/app/billing", label: "Abrechnung", icon: Receipt, group: "data", featureKey: "billingGoae" },
    { to: "/app/tasks", label: "Aufgaben", icon: CheckSquare, group: "data" },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, group: "data", featureKey: "analyticsDashboard" },
    { to: "/app/recall", label: "Recall", icon: Bell, group: "data", featureKey: "patientReminders" },
    { to: "/app/follow-ups", label: "Wiedervorlagen", icon: RotateCcw, group: "data", featureKey: "patientReminders" },
    {
        to: "/app/referrals",
        label: "Überweisungen",
        icon: ArrowRightLeft,
        badge: "referrals",
        group: "data",
        parentTo: "/app/netzwerk",
    },
    { to: "/app/consents", label: "Aufklärungen", icon: ShieldCheck, group: "data", featureKey: "documentSharing" },
    { to: "/app/schedule", label: "Dienstplan", icon: CalendarRange, group: "data" },
    {
        to: "/app/visits/pending-approvals",
        label: "Visiten-Anordnungen",
        icon: ClipboardCheck,
        badge: "pendingApprovals",
        group: "data",
    },
];

const NAV_GROUPS = [
    { key: "main", label: "Navigation" },
    { key: "communication", label: "Kommunikation" },
    { key: "data", label: "Verwaltung" },
] as const;

const BOTTOM_ITEMS = [
    { to: "/app/settings", label: "Einstellungen", icon: Settings, shortcut: "9" },
] as const;

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
    isDesktop?: boolean;
}

export function Sidebar({
    collapsed,
    onToggle,
    mobileOpen,
    onMobileClose,
    isDesktop = false,
}: SidebarProps) {
    const { user, logout } = useAuth();
    const { isFeatureEnabled, loading: featureGateLoading } = useFeatureGateContext();
    const { allowed: canSeeAnalytics, loading: analyticsPermissionLoading } = usePermission("analytics.read");
    const location = useLocation();
    const navigate = useNavigate();
    const { theme, setTheme, resolvedTheme } = useThemeMode();
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // Badges using the global React Query store! Zero custom socket logic here.
    const { data: ticketStats } = useTicketStatsQuery(user?.practiceId);
    const { data: inboxStats } = useInboxStatsQuery(user?.practiceId);
    const { data: phoneStats } = usePhoneStatsQuery(user?.practiceId);
    const { data: careRequestStats } = useCareRequestStatsQuery(user?.practiceId);
    const { data: escalationStats } = useEscalationStatsQuery(user?.practiceId);
    const { data: referralNotificationCounts } = useReferralNotificationCountsQuery(
        user?.practiceId,
    );
    const { data: pendingApprovalsStats } = usePendingApprovalsCountQuery(Boolean(user));

    async function handleLogout() {
        await logout();
        navigate("/login", { replace: true });
    }

    const getBadgeCount = (type: string) => {
        switch (type) {
            case "tickets":
                return ticketStats?.open || 0;
            case "unread":
                return inboxStats?.unread || 0;
            case "missed":
                return phoneStats?.missedCalls || 0;
            case "careRequests":
                return careRequestStats?.total || 0;
            case "escalations":
                return escalationStats?.awaiting || 0;
            case "referrals":
                return referralNotificationCounts?.total || 0;
            case "pendingApprovals":
                return pendingApprovalsStats?.total || 0;
            default:
                return 0;
        }
    };

    const isItemVisible = (item: NavItem) => {
        if (item.to === "/app/analytics") {
            if (analyticsPermissionLoading) return false;
            if (!canSeeAnalytics) return false;
        }
        if (!item.featureKey) return true;
        if (featureGateLoading) return false;
        return isFeatureEnabled(item.featureKey);
    };

    const getItemChildren = (item: NavItem, items: NavItem[]) =>
        items.filter((candidate) => candidate.parentTo === item.to);

    const isItemActive = (item: NavItem, items: NavItem[]) => {
        const children = getItemChildren(item, items);
        return [item, ...children].some((candidate) => location.pathname.startsWith(candidate.to));
    };

    // Keyboard shortcuts: Cmd+1 to Cmd+5
    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (!e.metaKey && !e.ctrlKey) return;
            const allItems = [...NAV_ITEMS, ...BOTTOM_ITEMS];
            const item = allItems.find((i) => "shortcut" in i && i.shortcut === e.key);
            if (item) {
                e.preventDefault();
                window.location.hash = ""; // Clear any anchor
                const link = document.querySelector(`a[href="${item.to}"]`) as HTMLAnchorElement;
                link?.click();
            }
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const initials = user
        ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
        : "??";

    // Close mobile sidebar on route change
    useEffect(() => {
        if (mobileOpen) onMobileClose?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const ThemeIcon = resolvedTheme === "dark" ? Moon : Sun;
    const detailReveal = {
        type: "spring",
        stiffness: 360,
        damping: 34,
        mass: 0.7,
    } as const;

    const cycleTheme = () => {
        const next = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
        setTheme(next);
    };

    return (
        <aside
            aria-label="Hauptnavigation"
            className={cn(
                "fixed inset-y-0 left-0 z-50 flex flex-col dark:bg-neutral-800",
                "border-r dark:border-neutral-700 overflow-hidden",
                // Mobile: slide in/out (transform only — no layout recalc)
                "max-lg:-translate-x-full max-lg:w-[280px] max-lg:transition-transform max-lg:duration-300",
                mobileOpen && "max-lg:translate-x-0",
                "max-lg:shadow-elevated",
            )}
            style={{
                backgroundColor: 'var(--surface-sidebar)',
                borderColor: 'var(--border-subtle)',
                ...(isDesktop ? {
                    width: collapsed ? 68 : 260,
                    transition: "width 300ms cubic-bezier(0.32, 0.72, 0, 1)",
                } : {}),
            }}
        >
            {/* Floating Toggle Button (Desktop) */}
            <button
                onClick={onToggle}
                className="absolute -right-3.5 top-5 z-50 hidden h-7 w-7 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 shadow-sm transition-all hover:scale-105 hover:bg-neutral-50 hover:text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200 lg:flex"
                aria-label={collapsed ? "Sidebar einblenden" : "Sidebar ausblenden"}
            >
                <ChevronLeft
                    className={cn(
                        "h-4 w-4 transition-transform duration-300",
                        collapsed && "rotate-180",
                    )}
                />
            </button>

            {/* Logo */}
            <div className="flex h-16 shrink-0 items-center gap-3 px-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 shadow-soft">
                    <Activity className="h-[18px] w-[18px] text-white" />
                </div>
                <motion.div
                    className="flex min-w-0 flex-col overflow-hidden origin-left"
                    initial={false}
                    animate={{
                        // maxWidth removed — sidebar overflow-hidden clips the content at 68px.
                        // opacity + x are GPU-compositable (no layout recalc).
                        opacity: collapsed ? 0 : 1,
                        x: collapsed ? -6 : 0,
                    }}
                    transition={detailReveal}
                    aria-hidden={collapsed}
                >
                    <span className="whitespace-nowrap text-sm font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
                        MediFlow
                    </span>
                    <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                        Praxis
                    </span>
                </motion.div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
                {NAV_GROUPS.map((group, gi) => {
                    const items = NAV_ITEMS.filter((i) => i.group === group.key).filter(isItemVisible);
                    const rootItems = items.filter((item) => !item.parentTo);
                    if (items.length === 0) return null;
                    return (
                        <div key={group.key} className={cn(gi > 0 && "mt-3")}>
                            {/* Section divider label */}
                            {!collapsed ? (
                                <p className="px-3 mb-1 text-2xs font-semibold uppercase tracking-wider text-neutral-300 dark:text-neutral-600 select-none">
                                    {group.label}
                                </p>
                            ) : gi > 0 ? (
                                <div className="mx-2 mb-2 h-px bg-neutral-100 dark:bg-neutral-700" />
                            ) : null}

                            <div className="space-y-0.5">
                                {rootItems.map((item) => {
                                    const isActive = isItemActive(item, items);
                                    const isDirectlyActive = location.pathname.startsWith(item.to);
                                    const childItems = getItemChildren(item, items);
                                    return (
                                        <div key={item.to} className="relative group/nav">
                                            <NavLink
                                                to={item.to}
                                                title={collapsed ? item.label : undefined}
                                                onMouseEnter={() => prefetchRoute(item.to)}
                                                onFocus={() => prefetchRoute(item.to)}
                                                className={cn(
                                                    "sidebar-item relative",
                                                    isActive && "sidebar-item-active",
                                                )}
                                            >
                                                {/* Sliding active indicator bar */}
                                                {isDirectlyActive && (
                                                    <motion.span
                                                        layoutId="sidebar-active"
                                                        className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-brand-500 dark:bg-brand-400"
                                                        transition={{
                                                            type: "spring",
                                                            damping: 30,
                                                            stiffness: 300,
                                                        }}
                                                    />
                                                )}
                                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                                    <item.icon className="sidebar-icon h-5 w-5" />
                                                </span>
                                                <AnimatePresence>
                                                    {!collapsed && (
                                                        <motion.span
                                                            className="truncate"
                                                            initial={{ opacity: 0, x: -8 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -8 }}
                                                            transition={{ duration: 0.15 }}
                                                        >
                                                            {item.label}
                                                        </motion.span>
                                                    )}
                                                </AnimatePresence>
                                                <AnimatePresence>
                                                    {!collapsed &&
                                                        "badge" in item &&
                                                        item.badge &&
                                                        (() => {
                                                            const count = getBadgeCount(
                                                                item.badge as string,
                                                            );
                                                            return count > 0 ? (
                                                                <motion.span
                                                                    key="badge"
                                                                    className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-error/90 px-1.5 text-xs font-medium text-white tabular-nums shadow-sm"
                                                                    initial={{
                                                                        opacity: 0,
                                                                        scale: 0.8,
                                                                    }}
                                                                    animate={{
                                                                        opacity: 1,
                                                                        scale: 1,
                                                                    }}
                                                                    exit={{
                                                                        opacity: 0,
                                                                        scale: 0.8,
                                                                    }}
                                                                    transition={{ duration: 0.15 }}
                                                                >
                                                                    {count > 99 ? "99+" : count}
                                                                </motion.span>
                                                            ) : null;
                                                        })()}
                                                </AnimatePresence>
                                            </NavLink>

                                            {/* Collapsed tooltip */}
                                            {collapsed && (
                                                <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 opacity-0 scale-95 group-hover/nav:opacity-100 group-hover/nav:scale-100 transition-all duration-150 origin-left">
                                                    <div className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-neutral-900 dark:bg-neutral-100 px-2.5 py-1.5 text-xs font-medium text-white dark:text-neutral-900 shadow-elevated">
                                                        {item.label}
                                                        {"badge" in item &&
                                                            item.badge &&
                                                            (() => {
                                                                const count = getBadgeCount(
                                                                    item.badge as string,
                                                                );
                                                                return count > 0 ? (
                                                                    <span className="ml-1 rounded-full bg-error/90 px-1.5 py-0.5 text-2xs text-white tabular-nums">
                                                                        {count}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                    </div>
                                                </div>
                                            )}

                                            {!collapsed && childItems.length > 0 && (
                                                <div
                                                    role="group"
                                                    aria-label={`${item.label}-Unterpunkte`}
                                                    className="mt-1 ml-4 space-y-0.5 border-l border-neutral-200 pl-3 dark:border-neutral-700"
                                                >
                                                    {childItems.map((child) => {
                                                        const childIsActive = location.pathname.startsWith(child.to);
                                                        return (
                                                            <NavLink
                                                                key={child.to}
                                                                to={child.to}
                                                                onMouseEnter={() => prefetchRoute(child.to)}
                                                                onFocus={() => prefetchRoute(child.to)}
                                                                className={cn(
                                                                    "sidebar-item relative text-[13px]",
                                                                    childIsActive && "sidebar-item-active",
                                                                )}
                                                            >
                                                                {childIsActive && (
                                                                    <motion.span
                                                                        layoutId="sidebar-active"
                                                                        className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-brand-500 dark:bg-brand-400"
                                                                        transition={{
                                                                            type: "spring",
                                                                            damping: 30,
                                                                            stiffness: 300,
                                                                        }}
                                                                    />
                                                                )}
                                                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                                                    <child.icon className="sidebar-icon h-4 w-4" />
                                                                </span>
                                                                <span className="truncate">{child.label}</span>
                                                                {child.badge && (() => {
                                                                    const count = getBadgeCount(child.badge);
                                                                    return count > 0 ? (
                                                                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-error/90 px-1.5 text-xs font-medium text-white tabular-nums shadow-sm">
                                                                            {count > 99 ? "99+" : count}
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                            </NavLink>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </nav>

            {/* Bottom */}
            <div className="px-3 py-3 space-y-0.5" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                {/* Theme toggle */}
                <button
                    onClick={cycleTheme}
                    title={
                        theme === "system"
                            ? "System-Design"
                            : resolvedTheme === "dark"
                              ? "Dunkles Design"
                              : "Helles Design"
                    }
                    className="sidebar-item w-full"
                    style={{ minHeight: 44 }}
                >
                    {theme === "system" ? (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                            <Monitor className="sidebar-icon h-5 w-5" />
                        </span>
                    ) : (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                            <ThemeIcon className="sidebar-icon h-5 w-5" />
                        </span>
                    )}
                    <motion.span
                        className="truncate overflow-hidden whitespace-nowrap"
                        initial={false}
                        animate={{
                            opacity: collapsed ? 0 : 1,
                            x: collapsed ? -6 : 0,
                            // maxWidth removed — sidebar overflow clips at 68px (compositable-only now)
                        }}
                        transition={detailReveal}
                        aria-hidden={collapsed}
                    >
                        {theme === "light" ? "Hell" : theme === "dark" ? "Dunkel" : "System"}
                    </motion.span>
                    <motion.kbd
                        className="ml-auto overflow-hidden rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-xs font-medium text-neutral-400 dark:border-neutral-600 dark:bg-neutral-700"
                        initial={false}
                        animate={{
                            opacity: collapsed ? 0 : 1,
                            scale: collapsed ? 0.92 : 1,
                            // maxWidth removed — compositable opacity+scale only
                        }}
                        transition={detailReveal}
                        aria-hidden={collapsed}
                    >
                        ⌘D
                    </motion.kbd>
                </button>

                {BOTTOM_ITEMS.map((item) => {
                    const isActive = location.pathname.startsWith(item.to);
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            title={collapsed ? item.label : undefined}
                            onMouseEnter={() => prefetchRoute(item.to)}
                            onFocus={() => prefetchRoute(item.to)}
                            className={cn("sidebar-item", isActive && "sidebar-item-active")}
                        >
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                <item.icon className="sidebar-icon h-5 w-5" />
                            </span>
                            <AnimatePresence>
                                {!collapsed && (
                                    <motion.span
                                        className="truncate"
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -8 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        {item.label}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </NavLink>
                    );
                })}

                {/* User */}
                {collapsed ? (
                    <div className="mt-2 px-3 py-3">
                        <div className="flex min-h-[44px] items-center">
                            <span className="relative flex h-5 w-5 shrink-0 items-center justify-center overflow-visible">
                                <span className="absolute flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-100 to-brand-200 text-xs font-bold text-brand-700 shadow-sm dark:from-brand-800 dark:to-brand-700 dark:text-brand-200">
                                    {initials}
                                </span>
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="mt-2 flex items-center gap-3 rounded-xl bg-neutral-50/80 px-3 py-3 transition-colors duration-200 dark:bg-neutral-700/40">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-100 to-brand-200 text-xs font-bold text-brand-700 shadow-sm dark:from-brand-800 dark:to-brand-700 dark:text-brand-200">
                            {initials}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="truncate whitespace-nowrap text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                                {user?.firstName} {user?.lastName}
                            </p>
                            <p className="truncate whitespace-nowrap text-xs text-neutral-400 dark:text-neutral-500">
                                {user?.email || "Praxis-Team"}
                            </p>
                        </div>
                        <button
                            onClick={() => setShowLogoutConfirm(true)}
                            className="ml-1 shrink-0 rounded-lg p-2 text-neutral-400 transition-all duration-200 hover:bg-white hover:text-neutral-600 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-neutral-600 dark:hover:text-neutral-200"
                            aria-label="Abmelden"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <ConfirmDialog
                    open={showLogoutConfirm}
                    title="Abmelden bestätigen"
                    message="Möchtest du dich wirklich abmelden?"
                    onConfirm={handleLogout}
                    onClose={() => setShowLogoutConfirm(false)}
                    confirmLabel="Abmelden"
                    cancelLabel="Abbrechen"
                    variant="danger"
                />
            </div>
        </aside>
    );
}
